/**
 * Phase 4+ — Override Governance Panel
 * 
 * Allows LINK Ops roles to apply overrides with required:
 * - Reason text
 * - User identity (auto-captured)
 * - Timestamp (auto-captured)
 * 
 * Overrides:
 * - Must never change surgeon dictation or attestation
 * - Must be clearly flagged in audit views
 * - Are only available to LINK Ops roles
 */

import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertTriangle,
  Shield,
  Lock,
  FileWarning,
  History,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComplianceOverride, OverrideType } from '@/lib/compliance/types';
import { toast } from 'sonner';

interface OverrideGovernancePanelProps {
  caseId: string;
  overrides: ComplianceOverride[];
  userRole: string;
  userEmail?: string;
  isDemoMode: boolean;
  onApplyOverride: (override: {
    overrideType: OverrideType;
    reason: string;
    cptCode?: string;
    previousValue: string;
    newValue: string;
  }) => Promise<boolean>;
}

const OVERRIDE_TYPE_LABELS: Record<OverrideType, string> = {
  modifier_override: 'Modifier Override',
  documentation_status_override: 'Documentation Status Override',
  export_gate_bypass: 'Export Gate Bypass',
  sequencing_override: 'Sequencing Override',
  completeness_override: 'Completeness Override',
};

export function OverrideGovernancePanel({
  caseId,
  overrides,
  userRole,
  userEmail,
  isDemoMode,
  onApplyOverride,
}: OverrideGovernancePanelProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [overrideType, setOverrideType] = useState<OverrideType>('completeness_override');
  const [reason, setReason] = useState('');
  const [cptCode, setCptCode] = useState('');
  const [previousValue, setPreviousValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Only LINK Ops roles can apply overrides
  const canOverride = userRole === 'link_ops' || userRole === 'link_ops_manager' || userRole === 'super_admin';

  const handleSubmit = useCallback(async () => {
    if (!reason.trim()) {
      toast.error('Override reason is required');
      return;
    }
    if (reason.trim().length < 10) {
      toast.error('Override reason must be at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await onApplyOverride({
        overrideType,
        reason: reason.trim(),
        cptCode: cptCode || undefined,
        previousValue,
        newValue,
      });

      if (success) {
        toast.success('Override applied', {
          description: 'Override logged in audit trail',
        });
        setShowDialog(false);
        setReason('');
        setCptCode('');
        setPreviousValue('');
        setNewValue('');
      }
    } catch (err) {
      toast.error('Failed to apply override');
    } finally {
      setIsSubmitting(false);
    }
  }, [overrideType, reason, cptCode, previousValue, newValue, onApplyOverride]);

  return (
    <>
      <Card className={cn('border-border', overrides.length > 0 && 'border-warning/50')}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-primary" />
                Override Governance
                {isDemoMode && (
                  <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">
                    Demo
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Overrides require justification and are permanently recorded in the audit trail
              </CardDescription>
            </div>
            {canOverride && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDialog(true)}
                className="gap-1 text-xs"
              >
                <FileWarning className="h-3 w-3" />
                Apply Override
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {!canOverride && (
            <Alert className="border-muted">
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Only LINK Ops roles can apply overrides. Overrides do not modify surgeon dictation or attestation.
              </AlertDescription>
            </Alert>
          )}

          {/* Override history */}
          {overrides.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <History className="h-3 w-3" />
                Override History ({overrides.length})
              </p>
              {overrides.map((override) => (
                <div
                  key={override.id}
                  className="p-3 rounded-lg border border-warning/30 bg-warning/5 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">
                      {OVERRIDE_TYPE_LABELS[override.overrideType]}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(override.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs">{override.reason}</p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>By: {override.actorEmail}</span>
                    <span>•</span>
                    <span>Role: {override.actorRole}</span>
                    {override.cptCode && (
                      <>
                        <span>•</span>
                        <span>CPT: {override.cptCode}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-success" />
              No overrides applied — all decisions are standard workflow
            </div>
          )}

          {/* Safety notice */}
          <div className="p-2 bg-muted/30 rounded text-[10px] text-muted-foreground leading-relaxed flex items-start gap-2">
            <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
            <p>
              Overrides never change surgeon dictation, attestation, or clinical content. 
              All overrides are flagged in audit exports and compliance reviews.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Apply Override
            </DialogTitle>
            <DialogDescription>
              This override will be permanently recorded in the audit trail with your identity and justification.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert className="border-warning/50 bg-warning/10">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning text-sm">Override Safety</AlertTitle>
              <AlertDescription className="text-xs">
                Overrides cannot modify surgeon dictation or attestation. Your identity, role, and reason will be permanently logged.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Override Type</Label>
              <Select value={overrideType} onValueChange={(v) => setOverrideType(v as OverrideType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(OVERRIDE_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>CPT Code (optional)</Label>
              <input
                type="text"
                placeholder="e.g., 29827"
                value={cptCode}
                onChange={(e) => setCptCode(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              />
            </div>

            <div className="space-y-2">
              <Label>Reason for Override *</Label>
              <Textarea
                placeholder="Provide a clear justification for this override (minimum 10 characters)..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                {reason.length}/10 minimum characters • Logged permanently
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-2 bg-muted/30 rounded">
                <p className="font-medium text-muted-foreground">Actor</p>
                <p>{userEmail || 'Current user'}</p>
              </div>
              <div className="p-2 bg-muted/30 rounded">
                <p className="font-medium text-muted-foreground">Role</p>
                <p>{userRole}</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!reason.trim() || reason.trim().length < 10 || isSubmitting}
              className="gap-2 bg-warning text-warning-foreground hover:bg-warning/90"
            >
              <AlertTriangle className="h-4 w-4" />
              {isSubmitting ? 'Applying...' : 'Apply Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
